'use client'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CardWatermark } from '@/components/ui/card-watermark'
import { Icons } from '@/components/ui/icons'
import { Skeleton } from '@/components/ui/skeleton'
import { useState, useEffect, useCallback } from 'react'
import { apiClient } from '@/lib/api-client'
import { appendScopeFilterParams } from '@/lib/dashboard-scope-filters'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
} from 'recharts'

interface WeeklyStats {
  week_number: number
  week_start?: string | null
  week_end?: string | null
  call_count: number
  average_score: number
  fatal_count: number
  fatal_percentage: number
}

interface DailyStats {
  date: string
  day_name: string
  call_count: number
  fatal_count: number
  non_fatal_count: number
  fatal_percentage: number
  average_score: number
}

interface WeekWiseViewProps {
  weeklyStats?: WeeklyStats[]
  isLoading?: boolean
  startDate?: string
  endDate?: string
  targetEmployeeId?: number
  selectedProduct?: string
  selectedSite?: string
}

const FATAL_COLOR = '#ef4444'
const NON_FATAL_COLOR = '#10b981'

interface FatalTooltipProps {
  active?: boolean
  payload?: Array<{
    name: string
    value: number
    color: string
    payload: {
      label: string
      call_count?: number
      fatal_count?: number
      fatal_percentage?: number
    }
  }>
}

function FatalBarTooltip({ active, payload }: FatalTooltipProps) {
  if (!active || !payload?.length) return null
  const row = payload[0].payload
  const fatal = row.fatal_count ?? payload.find((p) => p.name === 'Fatal')?.value ?? 0
  const total = row.call_count ?? payload.reduce((s, p) => s + p.value, 0)

  return (
    <div className='rounded-xl border border-white/60 bg-white/95 p-3 shadow-float backdrop-blur-sm'>
      <p className='mb-2 text-xs font-medium text-brand-navy'>{row.label}</p>
      <p className='text-xs text-muted-foreground'>
        Audit count: <span className='font-semibold text-brand-navy'>{total}</span>
      </p>
      <p className='text-xs text-red-600'>
        Fatal: <span className='font-semibold'>{fatal}</span>
      </p>
      {row.fatal_percentage != null && (
        <p className='text-xs text-muted-foreground'>
          Fatal %: <span className='font-semibold text-brand-navy'>{row.fatal_percentage}%</span>
        </p>
      )}
    </div>
  )
}

interface LineTooltipProps {
  active?: boolean
  payload?: Array<{
    name: string
    value: number
    color: string
    payload: { week: string }
  }>
}

function LineChartTooltip({ active, payload }: LineTooltipProps) {
  if (!active || !payload?.length) return null

  return (
    <div className='rounded-xl border border-white/60 bg-white/95 p-3 shadow-float backdrop-blur-sm'>
      <p className='mb-2 text-xs font-medium text-brand-navy'>{payload[0].payload.week}</p>
      <div className='space-y-1'>
        {payload.map((entry, index) => (
          <div key={index} className='flex items-center gap-2 text-xs'>
            <div className='h-2 w-2 rounded-full' style={{ backgroundColor: entry.color }} />
            <span className='text-muted-foreground'>{entry.name}:</span>
            <span className='font-semibold text-brand-navy'>
              {entry.name.includes('Score') ? `${entry.value}%` : entry.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

function formatDayLabel(date: string, dayName: string) {
  const d = new Date(date)
  const dd = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
  return `${dayName} ${dd}`
}

export function WeekWiseView({
  isLoading: initialLoading = false,
  startDate = '',
  endDate = '',
  targetEmployeeId,
  selectedProduct,
  selectedSite,
}: WeekWiseViewProps) {
  const [weeklyStats, setWeeklyStats] = useState<WeeklyStats[]>([])
  const [dailyStats, setDailyStats] = useState<DailyStats[]>([])
  const [isLoading, setIsLoading] = useState(initialLoading)

  const fetchStats = useCallback(async () => {
    if (!startDate || !endDate) return

    try {
      setIsLoading(true)
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate })
      if (targetEmployeeId) params.set('target_employee_id', String(targetEmployeeId))
      appendScopeFilterParams(params, selectedProduct, selectedSite)

      const [weekly, daily] = await Promise.all([
        apiClient.get<WeeklyStats[]>(`/api/analysis/stats/weekly?${params}`),
        apiClient.get<DailyStats[]>(`/api/analysis/stats/daily?${params}`),
      ])
      setWeeklyStats(weekly)
      setDailyStats(daily)
    } catch (error) {
      console.error('Failed to fetch period stats:', error)
      setWeeklyStats([])
      setDailyStats([])
    } finally {
      setIsLoading(false)
    }
  }, [startDate, endDate, targetEmployeeId, selectedProduct, selectedSite])

  useEffect(() => {
    if (startDate && endDate) {
      fetchStats()
    }
  }, [startDate, endDate, fetchStats])

  if (isLoading) {
    return (
      <div className='space-y-6'>
        <div className='grid gap-6 lg:grid-cols-2'>
          <Skeleton className='h-[320px] w-full' />
          <Skeleton className='h-[320px] w-full' />
        </div>
        <Skeleton className='h-[320px] w-full' />
      </div>
    )
  }

  const hasWeekly = weeklyStats.length > 0
  const hasDaily = dailyStats.length > 0

  const weeklyChartData = weeklyStats.map((week) => ({
    week: `Week ${week.week_number}`,
    label: `Week ${week.week_number}`,
    weekNum: week.week_number,
    calls: week.call_count,
    score: week.average_score,
    fatal: week.fatal_count,
    nonFatal: Math.max(week.call_count - week.fatal_count, 0),
    call_count: week.call_count,
    fatal_count: week.fatal_count,
    fatal_percentage: week.fatal_percentage,
  }))

  const dailyChartData = dailyStats.map((day) => ({
    day: formatDayLabel(day.date, day.day_name),
    label: formatDayLabel(day.date, day.day_name),
    fatal: day.fatal_count,
    nonFatal: day.non_fatal_count,
    call_count: day.call_count,
    fatal_count: day.fatal_count,
    fatal_percentage: day.fatal_percentage,
  }))

  if (!hasWeekly && !hasDaily) {
    return (
      <Card className='relative overflow-hidden'>
        <CardWatermark opacity={3} />
        <CardContent className='flex flex-col items-center justify-center py-12'>
          <Icons.calendar className='mb-3 h-12 w-12 text-gray-200' />
          <h3 className='font-semibold text-brand-navy'>No period data available</h3>
          <p className='mt-1 text-sm text-muted-foreground'>
            Statistics will appear here for your selected date range
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className='space-y-6'>
      <div className='grid gap-6 lg:grid-cols-2'>
        {/* Calls & Score Trend */}
        <Card className='relative overflow-hidden'>
          <CardWatermark opacity={3} scale={1.1} />
          <CardHeader className='relative z-10 pb-2'>
            <CardTitle className='text-base'>Calls & Average Score</CardTitle>
          </CardHeader>
          <CardContent className='relative z-10 pt-2'>
            {!hasWeekly ? (
              <div className='flex h-[250px] items-center justify-center text-sm text-muted-foreground'>
                No weekly data
              </div>
            ) : (
              <ResponsiveContainer width='100%' height={250}>
                <LineChart data={weeklyChartData}>
                  <CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' />
                  <XAxis
                    dataKey='week'
                    tick={{ fill: '#7B8AB8', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: '#7B8AB8', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip content={<LineChartTooltip />} />
                  <Line
                    type='monotone'
                    dataKey='calls'
                    name='Calls'
                    stroke='#8AA2DF'
                    strokeWidth={2}
                    dot={{ fill: '#8AA2DF', r: 4 }}
                  />
                  <Line
                    type='monotone'
                    dataKey='score'
                    name='Avg Score'
                    stroke='#535EA4'
                    strokeWidth={2}
                    dot={{ fill: '#535EA4', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Fatal by Week */}
        <Card className='relative overflow-hidden'>
          <CardWatermark opacity={3} scale={1.1} />
          <CardHeader className='relative z-10 pb-2'>
            <CardTitle className='text-base'>Calls Fatal by Week</CardTitle>
            <p className='text-xs text-muted-foreground'>Fatal vs non-fatal calls per week</p>
          </CardHeader>
          <CardContent className='relative z-10 pt-2'>
            {!hasWeekly ? (
              <div className='flex h-[250px] items-center justify-center text-sm text-muted-foreground'>
                No weekly data
              </div>
            ) : (
              <ResponsiveContainer width='100%' height={250}>
                <BarChart data={weeklyChartData}>
                  <CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' />
                  <XAxis
                    dataKey='week'
                    tick={{ fill: '#7B8AB8', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: '#7B8AB8', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip content={<FatalBarTooltip />} />
                  <Legend
                    wrapperStyle={{ fontSize: 12 }}
                    formatter={(value) => (
                      <span className='text-xs text-brand-navy'>{value}</span>
                    )}
                  />
                  <Bar dataKey='fatal' name='Fatal' stackId='a' fill={FATAL_COLOR} radius={[0, 0, 0, 0]} />
                  <Bar dataKey='nonFatal' name='Non-Fatal' stackId='a' fill={NON_FATAL_COLOR} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Fatal by Day */}
      <Card className='relative overflow-hidden'>
        <CardWatermark opacity={3} scale={1.1} />
        <CardHeader className='relative z-10 pb-2'>
          <CardTitle className='text-base'>Calls Fatal by Day</CardTitle>
          <p className='text-xs text-muted-foreground'>Fatal vs non-fatal calls per day</p>
        </CardHeader>
        <CardContent className='relative z-10 pt-2'>
          {!hasDaily ? (
            <div className='flex h-[250px] items-center justify-center text-sm text-muted-foreground'>
              No daily data for selected range
            </div>
          ) : (
            <ResponsiveContainer width='100%' height={280}>
              <BarChart data={dailyChartData}>
                <CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' />
                <XAxis
                  dataKey='day'
                  tick={{ fill: '#7B8AB8', fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                  interval={dailyChartData.length > 14 ? 1 : 0}
                  angle={dailyChartData.length > 10 ? -35 : 0}
                  textAnchor={dailyChartData.length > 10 ? 'end' : 'middle'}
                  height={dailyChartData.length > 10 ? 50 : 30}
                />
                <YAxis
                  tick={{ fill: '#7B8AB8', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip content={<FatalBarTooltip />} />
                <Legend
                  wrapperStyle={{ fontSize: 12 }}
                  formatter={(value) => (
                    <span className='text-xs text-brand-navy'>{value}</span>
                  )}
                />
                <Bar dataKey='fatal' name='Fatal' stackId='b' fill={FATAL_COLOR} />
                <Bar dataKey='nonFatal' name='Non-Fatal' stackId='b' fill={NON_FATAL_COLOR} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
